import { I18nProvider as LinguiProvider } from "@lingui/react";
import { ReactNode, useCallback, useEffect } from "react";

import { formatInTimeZone } from "date-fns-tz";

import { i18nContext } from "@/contexts/i18nContext";
import useActiveCurrency from "@/hooks/useActiveCurrency";
import useActiveLanguage from "@/hooks/useActiveLanguage";
import useActiveLocale from "@/hooks/useActiveLocale";
import useTimezone from "@/hooks/useActiveTimezone";
import {
  formatCurrencyAbbreviated,
  formatDateRange as _formatDateRange,
  formatNumberAbbreviated,
} from "@/utils";
import { i18n } from "@lingui/core";
import { Currency } from "@/constants";

const LANGUAGE_MAP = {
  sv: "sv-SE",
  en: "en-US",
};

/**
 * I18nProvider
 * A common way to handle internationalization regardless of the user being logged in or not.
 */
const I18nProvider: React.FunctionComponent<{ children: ReactNode }> = ({
  children,
}) => {
  // Get currency and language
  const language = useActiveLanguage();
  const currency = useActiveCurrency(language);
  const locale = useActiveLocale(language);
  const timezone = useTimezone();

  useEffect(() => i18n.activate(language), [language]);

  const createFormatter = useCallback(
    (settings = {}) => new Intl.NumberFormat(LANGUAGE_MAP[language], settings),
    [language]
  );

  // Assign formatters
  const formatCurrency = useCallback(
    (n: number, c?: Currency) =>
      createFormatter({
        currency: c || currency,
        style: "currency",
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(n),
    [currency, createFormatter]
  );

  const formatNumber = useCallback(
    (n: number) =>
      createFormatter({
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(n),
    [createFormatter]
  );

  const abbreviateCurrency = useCallback(
    (n: number) =>
      formatCurrencyAbbreviated(n, language, currency, formatNumber),
    [currency, language, formatNumber]
  );

  const abbreviateNumber = useCallback(
    (n: number) => formatNumberAbbreviated(n, language, formatNumber),
    [language, formatNumber]
  );

  const formatDate = useCallback(
    (date: Date | string, dateFormat: string) =>
      formatInTimeZone(date, timezone, dateFormat, { locale }),
    [locale, timezone]
  );

  const formatDateRange = useCallback(
    (
      start: Date | string | undefined,
      end: Date | string | undefined,
      individualFormat: string
    ) => _formatDateRange(start, end, (d) => formatDate(d, individualFormat)),
    [formatDate]
  );

  return (
    <i18nContext.Provider
      value={{
        formatCurrency,
        formatNumber,
        abbreviateNumber,
        abbreviateCurrency,
        formatDateRange,
        formatDate,
        language,
        currency,
        locale,
      }}
    >
      <LinguiProvider i18n={i18n}>{children}</LinguiProvider>
    </i18nContext.Provider>
  );
};

export default I18nProvider;
